// =============================================================================
// PlanSection.js — Phase 82
//
// Current plan display + Figma-style comparison cards + real invoice history.
// Heavy checkout flows (Razorpay, cycle toggles, beta gating, pending upgrade
// staging) live in the dedicated /settings/billing page — we don't duplicate
// them here. The "Manage billing" / "Change plan" buttons route users there.
// =============================================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { effectivePlan, planMeta, isBetaActive, isOnTrial, trialDaysLeft } from '@/lib/entitlements';
import { formatMoney } from '@/lib/format';
import { SectionHeader, SettingsCard, PrimaryButton } from './primitives';

// Only allow http(s) URLs for the PDF invoice link. React strips javascript:
// automatically since 16.9, but this belt-and-braces check keeps us safe if a
// future refactor bypasses React (e.g. window.open) and against schemes React
// doesn't strip (e.g. data:).
function safePdfHref(url) {
  if (typeof url !== 'string' || url.length === 0) return null;
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.toString();
  } catch { /* invalid URL */ }
  return null;
}

const PLAN_TIERS = [
  {
    id: 'free',
    name: '2120 Free',
    price: '₹0',
    period: 'forever',
    features: ['Overview and Transactions', 'Basic briefing (weekly)', 'Up to 2 accounts', '3 months of history'],
  },
  {
    id: 'clarity',
    name: '2120 Clarity',
    price: '₹499',
    period: 'per month',
    features: ['Everything in Free', 'Daily Financial Briefing', 'All dashboard sections', 'AI pattern insights', 'Sunday Brief + Monthly Recap', 'Up to 5 accounts'],
  },
  {
    id: 'horizon',
    name: '2120 Horizon',
    price: '₹799',
    period: 'per month',
    features: ['Everything in Clarity', 'Longer history & projections', 'Advanced Plan-Ahead scenarios', 'Priority support', 'Up to 15 accounts'],
  },
  {
    id: 'summit',
    name: '2120 Summit',
    price: '₹1,299',
    period: 'per month',
    features: ['Everything in Horizon', 'Family & Sharing (2 seats)', 'Shared audit log', 'Concierge onboarding'],
  },
];

export default function PlanSection() {
  const { user, profile } = useAuth();
  const nav = useNavigate();
  const [showPlans, setShowPlans] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const currentPlan = effectivePlan(profile);
  const meta = planMeta(currentPlan);
  const onTrial = isOnTrial(profile);
  const onBeta = isBetaActive(profile);
  const daysLeft = trialDaysLeft(profile);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);
      if (!cancelled) {
        setInvoices(data || []);
        setLoadingInvoices(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const renewalText = onBeta
    ? 'Full access during private beta — no card required.'
    : onTrial
      ? `Trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining`
      : profile?.plan_renews_at
        ? `Renews ${new Date(profile.plan_renews_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}`
        : 'Manage your subscription and billing.';

  return (
    <div className="space-y-5" data-testid="settings-plan-section">
      <SectionHeader title="Plan" subtitle="Manage your subscription and billing." />

      {/* Current plan */}
      <SettingsCard className="overflow-hidden" testId="settings-current-plan">
        <div className="px-6 py-5 flex items-start justify-between gap-4 border-b border-border/60 flex-wrap sm:flex-nowrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-base font-semibold text-charcoal">2120 {meta.label}</p>
              <span
                className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-evergreen/10 text-evergreen"
                data-testid="settings-plan-badge"
              >
                Current
              </span>
              {onBeta && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--gold))]/20 text-foreground/80 border border-[hsl(var(--gold))]/30">
                  Private beta
                </span>
              )}
              {!onBeta && onTrial && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--gold))]/20 text-foreground/80 border border-[hsl(var(--gold))]/30">
                  Trial · {daysLeft} day{daysLeft === 1 ? '' : 's'} left
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{renewalText}</p>
          </div>
        </div>
        <div className="px-6 py-4 space-y-2.5">
          {(PLAN_TIERS.find((p) => p.id === currentPlan) || PLAN_TIERS[0]).features.map((f) => (
            <div key={f} className="flex items-center gap-2.5">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-700 dark:text-emerald-400" aria-hidden="true">
                <polyline points="2,6.5 5,9.5 11,3" />
              </svg>
              <span className="text-sm text-charcoal/80">{f}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-border/60 flex items-center gap-3 flex-wrap">
          <PrimaryButton onClick={() => setShowPlans(!showPlans)} variant="ghost" testId="settings-toggle-plans">
            {showPlans ? 'Hide plans' : 'Change plan'}
          </PrimaryButton>
          <PrimaryButton onClick={() => nav('/settings/billing')} variant="ghost" testId="settings-manage-billing">
            Manage billing
          </PrimaryButton>
          {currentPlan !== 'free' && !onBeta && (
            <button
              type="button"
              onClick={() => nav('/settings/billing')}
              className="ml-auto text-xs text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              data-testid="settings-cancel-link"
            >
              Cancel subscription
            </button>
          )}
        </div>
      </SettingsCard>

      {/* Plan comparison */}
      {showPlans && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="settings-plan-comparison">
          {PLAN_TIERS.map((plan) => {
            const isActive = plan.id === currentPlan;
            return (
              <SettingsCard
                key={plan.id}
                className={`p-5 flex flex-col ${isActive ? 'ring-2 ring-evergreen/40' : ''}`}
                testId={`settings-plan-tier-${plan.id}`}
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2">{plan.name}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <p className="font-display text-2xl font-medium text-charcoal">{plan.price}</p>
                  <p className="text-xs text-muted-foreground">{plan.period}</p>
                </div>
                <div className="space-y-2 mb-5 flex-1">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-start gap-2">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden="true">
                        <polyline points="2,6 4.5,8.5 10,2.5" />
                      </svg>
                      <span className="text-xs text-charcoal/80">{f}</span>
                    </div>
                  ))}
                </div>
                <PrimaryButton
                  variant={isActive ? 'ghost' : 'primary'}
                  size="sm"
                  onClick={() => nav('/pricing')}
                  disabled={isActive}
                  testId={`settings-plan-cta-${plan.id}`}
                >
                  {isActive ? 'Current plan' : 'Choose plan'}
                </PrimaryButton>
              </SettingsCard>
            );
          })}
        </div>
      )}

      {/* Billing history */}
      <SettingsCard className="px-6 py-5" testId="settings-invoices">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Recent billing</p>
        {loadingInvoices ? (
          <div className="space-y-2">
            <div className="h-8 bg-offwhite/60 rounded-lg animate-pulse" />
            <div className="h-8 bg-offwhite/60 rounded-lg animate-pulse" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground/70 py-3">No invoices yet. When you subscribe, receipts will appear here.</p>
        ) : (
          <div className="space-y-0">
            {invoices.map((row, i) => {
              const amount = Number(row.amount || 0);
              const currency = (row.currency || profile?.base_currency || 'INR').toUpperCase();
              const status = row.status || 'paid';
              return (
                <div
                  key={row.id || i}
                  className="flex items-center gap-4 py-3"
                  style={{ borderBottom: i < invoices.length - 1 ? '1px solid hsl(var(--border) / 0.6)' : 'none' }}
                  data-testid={`settings-invoice-row-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal truncate">{row.description || `2120 ${meta.label}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </p>
                  </div>
                  <p className="text-sm font-medium text-charcoal tabular-nums">{formatMoney(amount, currency)}</p>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide ${status === 'paid' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}
                  >
                    {status}
                  </span>
                  {(() => {
                    const safe = safePdfHref(row.pdf_url);
                    return safe ? (
                      <a href={safe} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground/70 hover:text-charcoal transition-colors">PDF</a>
                    ) : null;
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
